/* prettier-ignore-file */
/**
 * Wizard step 3 — transportation.
 *
 * Delivery and home pickup are independent legs; each is charged a full one-way
 * amount, so selecting both doubles the transportation cost. In-store pickup is
 * free and is auto-managed: it turns off once both other legs are selected,
 * mirroring the sync effect at NewBooking.js:749.
 *
 * IMPORTANT: onChange emits the useNewBookingStore field names --
 * `deliveryNeeded` / `homePickupNeeded`, NOT `delivery` / `homePickup`. The draft
 * is merged into the store with updateBookingData(), so a mismatched key writes a
 * junk field and the real flag never flips. The props are named delivery/homePickup
 * because that is what they mean locally; only the emitted keys must match the store.
 *
 * Rate, discount-tier and info blurb behaviour is carried over from
 * NewBooking.js:2743-3097, with one fix: discount tiers are compared numerically in
 * cents. NewBooking.js:2997 compares two centsToDollars() strings, so "100.00" >=
 * "20.00" is false and tiers mis-highlight.
 *
 * Distance uses the getDistance callable, the same path real bookings take today
 * (NewBooking.js:1436). The Calculator modal computes distance client-side instead,
 * because it lets the user supply an arbitrary shop address and getDistance
 * hardcodes its origin.
 */

import React, { useState } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  TextInput,
  ActivityIndicator,
} from "react-native";
import { getFunctions, httpsCallable } from "firebase/functions";
import { app } from "../../firebase.config";
import GoogleAddressInput from "../GoogleAddressInput";
import AddressValidationModal from "../AddressValidationModal";
import { formatCentsAsUSD, capitalizeFirstLetter } from "../../utils/formatting";
import { readPricingSettings } from "../../utils/rentalPricing";

const functions = getFunctions(app);

/** Collapsible info blurb: 3 lines, then "Show More" (NewBooking.js:2757-2769). */
const InfoBlurb = ({ text, accent, background }) => {
  const [expanded, setExpanded] = useState(false);
  if (!text) return null;
  const lines = text.split("\n");
  const isLong = lines.length > 3;
  return (
    <View
      style={[
        styles.infoMessageContainer,
        background ? { backgroundColor: background } : null,
        accent ? { borderLeftColor: accent } : null,
      ]}
    >
      <Text style={styles.infoMessageText}>
        {expanded ? text : lines.slice(0, 3).join("\n") + (isLong ? "..." : "")}
      </Text>
      {isLong && (
        <TouchableOpacity dataSet={{ hover: "lift" }} onPress={() => setExpanded(!expanded)} style={styles.infoToggleButton}>
          <Text style={styles.infoToggleText}>{expanded ? "▼ Show Less" : "▶ Show More"}</Text>
        </TouchableOpacity>
      )}
    </View>
  );
};

const StepTransportation = ({
  delivery,
  homePickup,
  inStorePickup,
  addressInput,
  addressSelected,
  addressConfirmed,
  gatedCommunity,
  communityName,
  addressNotes,
  miles,
  quote,
  settings,
  onChange,
}) => {
  const [checking, setChecking] = useState(false);
  const [distanceError, setDistanceError] = useState(null);
  // Distance is fetched before the map is shown, but only committed to the draft once
  // the customer confirms the route looks right.
  const [pendingMiles, setPendingMiles] = useState(null);
  const [showMapModal, setShowMapModal] = useState(false);
  // Distance-lookup failure, shown inside the modal rather than behind it.
  const [lookupError, setLookupError] = useState(null);

  const cfg = readPricingSettings(settings);
  const needsAddress = delivery || homePickup;
  // At least one leg at the shop -- the same condition as the derived inStorePickup.
  const usesStore = !delivery || !homePickup;
  // Address picked from the suggestions but never run through the distance check.
  // This is the one dead end in the step -- Next is blocked and the reason sat in
  // grey hint text, so both the callout and the button shout while it holds.
  const needsConfirm = needsAddress && addressSelected && !addressConfirmed;

  const legPriceLabel =
    miles != null && quote?.oneWayCents
      ? `${formatCentsAsUSD(quote.oneWayCents)} each way`
      : "Charged by distance — confirm your address below";

  const handleClearAddress = () => {
    setDistanceError(null);
    onChange({ addressInput: "", addressSelected: false, addressConfirmed: false, miles: null });
  };

  const handleValidateAddress = async () => {
    if (!addressSelected || !addressInput.trim()) return;

    // Open FIRST, before the lookup. A transparent RN Modal swallows touches behind
    // it, so this is what blocks the screen while the request is in flight —
    // otherwise the user can keep editing the address, or hit Next, mid-request.
    // The map begins plotting from the address immediately; the distance arrives
    // separately and the two loading states resolve independently.
    setDistanceError(null);
    setLookupError(null);
    setPendingMiles(null);
    setShowMapModal(true);
    setChecking(true);

    try {
      const getDistanceFn = httpsCallable(functions, "getDistance");
      const result = await getDistanceFn({ fullAddress: addressInput.trim() });
      const resolved = result.data?.distanceMiles ?? result.data?.miles;
      if (resolved == null) throw new Error("No distance returned");
      // Held locally, not committed to the draft — confirmation is the customer's
      // call after seeing the route. Autocomplete will happily return a real address
      // in the wrong town, and the fee is charged per mile.
      setPendingMiles(Number(Number(resolved).toFixed(1)));
    } catch (error) {
      console.error("❌ Distance lookup failed:", error);
      setLookupError("Could not work out the distance to that address.");
    } finally {
      setChecking(false);
    }
  };

  const handleConfirmFromMap = () => {
    setShowMapModal(false);
    onChange({ miles: pendingMiles, addressConfirmed: true });
  };

  const handleRejectFromMap = () => {
    setShowMapModal(false);
    setPendingMiles(null);
    onChange({ addressConfirmed: false, miles: null });
    setDistanceError(
      lookupError
        ? "Could not confirm that address. Check it and try again."
        : "Address not confirmed. Edit it above and check again."
    );
    setLookupError(null);
  };

  /**
   * The two legs are chosen independently, and inStorePickup is DERIVED, never
   * picked directly: it means "at least one leg happens at the shop", which is
   * exactly !(delivery && homePickup). See getInStorePickupLabel() at
   * NewBooking.js:700, whose label changes with the same combination.
   *
   * All four combinations are valid:
   *   shop  -> shop   delivery=F homePickup=F  inStorePickup=T
   *   deliver -> shop delivery=T homePickup=F  inStorePickup=T  (in-store dropoff)
   *   shop -> collect delivery=F homePickup=T  inStorePickup=T  (in-store pickup)
   *   deliver -> collect delivery=T homePickup=T inStorePickup=F
   */
  const setLegs = (nextDelivery, nextHomePickup) => {
    onChange({
      deliveryNeeded: nextDelivery,
      homePickupNeeded: nextHomePickup,
      inStorePickup: !(nextDelivery && nextHomePickup),
    });
  };

  const radio = (selected, label, onPress, sublabel) => (
    <TouchableOpacity style={styles.checkboxRow} onPress={onPress}>
      <View style={[styles.radio, selected && styles.radioSelected]}>
        {selected && <View style={styles.radioDot} />}
      </View>
      <View style={styles.checkboxTextBlock}>
        <Text style={styles.checkboxLabel}>{label}</Text>
        {sublabel ? <Text style={styles.checkboxSublabel}>{sublabel}</Text> : null}
      </View>
    </TouchableOpacity>
  );

  const checkbox = (checked, label, onPress, sublabel) => (
    <TouchableOpacity style={styles.checkboxRow} onPress={onPress}>
      <View style={[styles.checkbox, checked && styles.checkboxChecked]}>
        {checked && <Text style={styles.checkboxMark}>✓</Text>}
      </View>
      <View style={styles.checkboxTextBlock}>
        <Text style={styles.checkboxLabel}>{label}</Text>
        {sublabel ? <Text style={styles.checkboxSublabel}>{sublabel}</Text> : null}
      </View>
    </TouchableOpacity>
  );

  /** Transportation discount tiers, matched numerically in cents. */
  const renderDiscountTiers = () => {
    if (!cfg.useDeliveryDiscounts || cfg.deliveryDiscounts.length === 0) return null;

    const equipmentCents = quote?.productCostCents || 0;
    const applicable = cfg.deliveryDiscounts.filter(
      (d) => equipmentCents >= (d.from || 0) && equipmentCents <= (d.to || 0)
    );
    const highest = applicable.length
      ? applicable.reduce((max, d) => (d.value > max.value ? d : max), applicable[0])
      : null;

    return (
      <View style={styles.discountBlock}>
        <Text style={styles.discountHeading}>Available Transportation Discounts:</Text>
        {[...cfg.deliveryDiscounts]
          .sort((a, b) => (a.from || 0) - (b.from || 0))
          .map((discount, index) => {
            const isActive = highest && discount.id === highest.id;
            const amountNeededCents = equipmentCents < (discount.from || 0)
              ? (discount.from || 0) - equipmentCents
              : 0;
            return (
              <View key={discount.id || index} style={styles.discountTierRow}>
                <Text
                  style={[
                    styles.discountTierText,
                    isActive ? styles.discountTierActive : styles.discountTierInactive,
                  ]}
                >
                  {formatCentsAsUSD(discount.from || 0)}–{formatCentsAsUSD(discount.to || 0)} order:{" "}
                  {discount.value}% off delivery{isActive ? " ✓" : ""}
                </Text>
                {amountNeededCents > 0 && (
                  <Text style={styles.discountTierNeeded}>
                    {formatCentsAsUSD(amountNeededCents)} to unlock
                  </Text>
                )}
              </View>
            );
          })}
      </View>
    );
  };

  return (
    <ScrollView style={styles.container}>
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Getting it to you</Text>
        {radio(
          !delivery,
          "I'll pick up at the shop",
          () => setLegs(false, homePickup),
          "Free"
        )}
        {radio(delivery, "Deliver to me", () => setLegs(true, homePickup), legPriceLabel)}

        <View style={styles.legDivider} />

        <Text style={styles.sectionTitle}>Getting it back</Text>
        {radio(
          !homePickup,
          "I'll drop off at the shop",
          () => setLegs(delivery, false),
          "Free"
        )}
        {radio(
          homePickup,
          "Pick up from me at the end",
          () => setLegs(delivery, true),
          legPriceLabel
        )}

        {/* Pickup blurb whenever any leg happens at the shop. */}
        {usesStore && <InfoBlurb text={cfg.pickupInfo} accent="#1F8A5B" background="#E4F4EC" />}

        {/* Rates + discounts, shown once a paid leg is selected. */}
        {needsAddress && (
          <View style={styles.ratesBlock}>
            <Text style={styles.ratesTitle}>
              Delivery Rate ({formatCentsAsUSD(cfg.mileageRateCents)}/mile)
            </Text>
            <Text style={styles.ratesSubtext}>
              Loading/Unloading Fee: {formatCentsAsUSD(cfg.loadingFeeCents)}
            </Text>
            <Text style={styles.ratesSubtext}>
              Charged per trip. Delivery and pickup are billed separately.
            </Text>
            {renderDiscountTiers()}
          </View>
        )}

        {/* Delivery blurb appears once delivery or home pickup is ticked. */}
        {needsAddress && <InfoBlurb text={cfg.deliveryInfo} />}
      </View>

      {needsAddress && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Where are we going?</Text>

          <View style={styles.addressRow}>
            <GoogleAddressInput
              value={addressInput}
              onChangeText={(text) => onChange({ addressInput: text, addressSelected: false, addressConfirmed: false, miles: null })}
              onAddressSelected={(address) =>
                onChange({ addressInput: address, addressSelected: true, addressConfirmed: false, miles: null })
              }
              placeholder="Start typing your address..."
              style={styles.addressInputField}
              editable={!addressSelected}
              isValidated={addressConfirmed}
            />
            {addressSelected && (
              <TouchableOpacity dataSet={{ hover: "lift" }} style={styles.clearButton} onPress={handleClearAddress}>
                <Text style={styles.clearButtonText}>Clear</Text>
              </TouchableOpacity>
            )}
          </View>

          {/* Sits ABOVE the button on purpose: the instruction and the thing it
              points at read in order, so "tap the button below" is unambiguous. */}
          {needsConfirm && (
            <View style={styles.confirmAlert}>
              <Text style={styles.confirmAlertIcon}>⚠️</Text>
              <View style={styles.confirmAlertBody}>
                <Text style={styles.confirmAlertTitle}>Confirm your address to continue</Text>
                <Text style={styles.confirmAlertText}>
                  We can't work out your distance or transportation cost until you do — tap the
                  button below.
                </Text>
              </View>
            </View>
          )}

          <TouchableOpacity dataSet={{ hover: "lift" }}
            style={[
              styles.validateButton,
              // Amber while this is the only thing standing between the customer
              // and the next step; back to the calm primary green once it's done.
              // Disabled stays last so it still wins while a lookup is in flight.
              needsConfirm && styles.validateButtonUrgent,
              (checking || !addressSelected) && styles.validateButtonDisabled,
            ]}
            onPress={handleValidateAddress}
            disabled={checking || !addressSelected}
          >
            {checking ? (
              <ActivityIndicator size="small" color="#FFFFFF" />
            ) : (
              <Text
                style={[
                  styles.validateButtonText,
                  needsConfirm && styles.validateButtonTextUrgent,
                ]}
              >
                {addressConfirmed
                  ? "✓ Address confirmed"
                  : "Confirm address & distance →"}
              </Text>
            )}
          </TouchableOpacity>

          {distanceError && <Text style={styles.errorText}>{distanceError}</Text>}

          {addressConfirmed && miles != null && (
            <View style={styles.distanceBox}>
              <Text style={styles.distanceText}>
                {miles} miles from the shop · {formatCentsAsUSD(quote?.oneWayCents || 0)} per trip
              </Text>
              {quote?.transportTotalCents > 0 && (
                <Text style={styles.distanceSubtext}>
                  Transportation total: {formatCentsAsUSD(quote.transportTotalCents)}
                  {quote.transportationDiscountCents > 0
                    ? ` (−${formatCentsAsUSD(quote.transportationDiscountCents)} discount applied)`
                    : ""}
                </Text>
              )}
            </View>
          )}

          {!addressSelected && (
            <Text style={styles.hintText}>
              Start typing your address and tap it from the suggestions below — a typed address alone
              isn't accepted.
            </Text>
          )}

          {checkbox(gatedCommunity, "Gated community", () => onChange({ gatedCommunity: !gatedCommunity }))}

          {gatedCommunity && (
            <TextInput
              style={styles.input}
              value={communityName}
              onChangeText={(text) => onChange({ communityName: capitalizeFirstLetter(text) })}
              placeholder="Community name"
              placeholderTextColor="#AEB8B6"
              autoCapitalize="sentences"
            />
          )}

          <Text style={styles.fieldLabel}>Delivery notes (optional)</Text>
          <TextInput
            style={[styles.input, styles.textArea]}
            value={addressNotes}
            onChangeText={(text) => onChange({ addressNotes: capitalizeFirstLetter(text) })}
            placeholder="Gate code, building, where to leave things..."
            placeholderTextColor="#AEB8B6"
            autoCapitalize="sentences"
            multiline
            numberOfLines={3}
          />
        </View>
      )}

      <AddressValidationModal
        visible={showMapModal}
        address={addressInput}
        shopAddress={cfg.startingAddress}
        miles={pendingMiles}
        busy={checking}
        lookupError={lookupError}
        onConfirm={handleConfirmFromMap}
        onReject={handleRejectFromMap}
      />
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1 },
  section: { backgroundColor: "#FFFFFF", borderRadius: 8, padding: 16, marginBottom: 12 },
  sectionTitle: { fontSize: 16, fontWeight: "bold", color: "#2E3A38", marginBottom: 12 },

  checkboxRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    paddingVertical: 12,
    minHeight: 48,
    cursor: "pointer",
  },
  checkbox: {
    width: 22,
    height: 22,
    borderRadius: 4,
    borderWidth: 2,
    borderColor: "#DDE3E2",
    marginRight: 10,
    alignItems: "center",
    justifyContent: "center",
  },
  checkboxChecked: { backgroundColor: "#0E7C6B", borderColor: "#0E7C6B" },
  checkboxMark: { color: "#FFFFFF", fontSize: 14, fontWeight: "bold" },
  checkboxTextBlock: { flex: 1 },

  radio: {
    width: 22,
    height: 22,
    borderRadius: 11,
    borderWidth: 2,
    borderColor: "#DDE3E2",
    marginRight: 10,
    alignItems: "center",
    justifyContent: "center",
  },
  radioSelected: { borderColor: "#0E7C6B" },
  radioDot: { width: 11, height: 11, borderRadius: 5, backgroundColor: "#0E7C6B" },
  legDivider: { height: 1, backgroundColor: "#EDF1F0", marginVertical: 14 },
  checkboxLabel: { fontSize: 14, color: "#2E3A38", fontWeight: "500" },
  checkboxSublabel: { fontSize: 12, color: "#5C6B68", marginTop: 2 },

  warningText: { fontSize: 12, color: "#B4761B", marginTop: 8 },
  errorText: { fontSize: 12, color: "#C4453D", marginTop: 8 },
  hintText: { fontSize: 12, color: "#8A9794", marginTop: 8 },

  ratesBlock: { marginTop: 14, paddingTop: 14, borderTopWidth: 1, borderTopColor: "#EDF1F0" },
  ratesTitle: { fontSize: 15, fontWeight: "bold", color: "#2E3A38" },
  ratesSubtext: { fontSize: 13, color: "#5C6B68", paddingTop: 4 },

  discountBlock: { marginTop: 12 },
  discountHeading: { fontSize: 14, fontWeight: "600", color: "#2E3A38", marginBottom: 10 },
  discountTierRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 3,
  },
  discountTierText: { fontSize: 13, flex: 1, paddingRight: 8 },
  discountTierActive: { color: "#1F8A5B", fontWeight: "700" },
  discountTierInactive: { color: "#8A9794" },
  discountTierNeeded: { fontSize: 11, color: "#B4761B" },

  infoMessageContainer: {
    backgroundColor: "#E3F2EF",
    borderLeftWidth: 4,
    borderLeftColor: "#0E7C6B",
    borderRadius: 5,
    padding: 12,
    marginTop: 10,
    marginBottom: 4,
  },
  infoMessageText: { fontSize: 13, color: "#2E3A38", lineHeight: 18 },
  infoToggleButton: { marginTop: 6 },
  infoToggleText: { fontSize: 12, color: "#0E7C6B", fontWeight: "600" },

  // GoogleAddressInput's own dropdown carries a high zIndex (see its comment on
  // containerOpen), but that only outranks siblings *within its own stacking
  // context*. Wrapping it in this row for the Clear button put the Confirm
  // button, hint text, etc. back in front of the dropdown because they became
  // its new siblings. Matching position+zIndex here lets the row itself win
  // against those later fields in the section, same as before the wrap.
  addressRow: { flexDirection: "row", alignItems: "stretch", position: "relative", zIndex: 99999 },
  addressInputField: {
    flex: 1,
    borderWidth: 1,
    borderColor: "#DDE3E2",
    borderRadius: 6,
    padding: 12,
    fontSize: 16,
    backgroundColor: "#FFFFFF",
  },
  clearButton: {
    marginLeft: 8,
    paddingHorizontal: 14,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: "#C4453D",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  },
  clearButtonText: { color: "#C4453D", fontSize: 13, fontWeight: "600" },
  validateButton: {
    backgroundColor: "#0E7C6B",
    borderRadius: 6,
    paddingVertical: 12,
    alignItems: "center",
    marginTop: 12,
    cursor: "pointer",
  },
  validateButtonDisabled: { backgroundColor: "#9FC5BE" },
  validateButtonText: { color: "#FFFFFF", fontSize: 14, fontWeight: "600" },
  // Loud state for the one action that blocks the step. Amber is the app's
  // "needs attention" (theme.warning), so the button reads as unfinished
  // business until confirming turns it back to primary green.
  validateButtonUrgent: {
    backgroundColor: "#B4761B",
    paddingVertical: 16,
    borderRadius: 8,
    boxShadow: "0 2px 8px rgba(180, 118, 27, 0.38)",
  },
  validateButtonTextUrgent: { fontSize: 16, fontWeight: "800", letterSpacing: 0.3 },

  confirmAlert: {
    flexDirection: "row",
    alignItems: "flex-start",
    backgroundColor: "#FDF3E3",
    borderLeftWidth: 4,
    borderLeftColor: "#B4761B",
    borderRadius: 6,
    padding: 12,
    marginTop: 12,
  },
  confirmAlertIcon: { fontSize: 16, marginRight: 10, lineHeight: 20 },
  confirmAlertBody: { flex: 1 },
  confirmAlertTitle: { fontSize: 14, fontWeight: "800", color: "#7A4E12", marginBottom: 3 },
  confirmAlertText: { fontSize: 13, color: "#7A4E12", lineHeight: 18 },

  distanceBox: { backgroundColor: "#E4F4EC", borderRadius: 6, padding: 12, marginTop: 12 },
  distanceText: { fontSize: 13, color: "#1F8A5B", fontWeight: "600" },
  distanceSubtext: { fontSize: 12, color: "#2E3A38", marginTop: 4 },

  fieldLabel: { fontSize: 12, color: "#5C6B68", marginBottom: 4, marginTop: 12 },
  input: {
    borderWidth: 1,
    borderColor: "#DDE3E2",
    borderRadius: 6,
    padding: 12,
    fontSize: 16,
    backgroundColor: "#FFFFFF",
    marginTop: 8,
  },
  textArea: { minHeight: 70, textAlignVertical: "top" },
});

export default StepTransportation;
